import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  MotifLabel,
  MotifSelect,
  MotifOption,
  MotifFormField,
  MotifInput,
  MotifProgressLoader,
  MotifErrorMessage,
  MotifChip,
  MotifButton,
} from "@ey-xd/motif-react";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useGetDistributionApiDataQuery } from "@/features/distributions/steps/calculationStepReducer";
import { useGetSubAccountsQuery } from "@/services/paymentStatus/paymentStatusReducer";
import { useLazySearchCreditorListDataQuery } from "@/services/creditors/creditorsReducers";
import { useLazyGetCreditorSummaryDataQuery } from "@/pages/CaseDetail/Creditor/CreditorSummaryView/CreditorSummaryReducer";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import MaskedInput from "@/pages/CaseDetail/Creditor/MaskedInput";
import "./Details.scss";
import { ActiveTabsOrder } from "@/menuData";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
const Details = ({
  goToNextTab,
  creditorValue,
  setCreditorValue,
}: {
  goToNextTab: () => void;
  creditorValue: string;
  setCreditorValue: (creditorValue: string) => void;
}) => {
  const { caseId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showField, setShowField] = useState<any>({});
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    getValues,
    setValue,
    reset,
    setError,
    trigger,
    clearErrors,
  } = useFormContext();
  const prefixList = [
    { label: "pages.paymentStatus.paymentSetup.details.cheque", value: "0" },

    { label: "pages.paymentStatus.paymentSetup.details.statement", value: "1" },

    { label: "pages.paymentStatus.paymentSetup.details.wire", value: "2" },

    { label: "pages.paymentStatus.paymentSetup.details.other", value: "3" },
  ];
  const paymentOptionValue = watch("paymentOption");
  const [creditorList, setCreditorList] = useState<any[]>([]);
  const [creditorContactInfo, setCreditorContactInfo] = useState<any>({});
  const [isDiscardVisible, setIsDiscardVisible] = useState(false);
  const {
    data: distributionData,
    isLoading: distributionDataLoading,
    error: distributionDataError,
  } = useGetDistributionApiDataQuery(caseId);
  const caseData = useSelector((state: any) => state.case);
  const currencyData = caseData?.caseUnits;
  const distributionList =
    (distributionData &&
      distributionData?.results?.map((item: any) => ({
        name: item.name,
        id: item.id,
      }))) ||
    [];
  const {
    data: subAccountList,
    error: subAccountListError,
    isLoading: isSubAccountListLoading,
  } = useGetSubAccountsQuery(caseId, { skip: !caseId });
  const [
    triggerCreditor,
    { isFetching: creditorLoading, error: creditorError },
  ] = useLazySearchCreditorListDataQuery();
  const [
    triggerCreditorSummary,
    { isFetching: isCreditorSummaryFetching, error: creditorSummaryError },
  ] = useLazyGetCreditorSummaryDataQuery();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: isDirty }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, isDirty]);

  useEffect(() => {
    const btn = document.querySelectorAll(
      ".motif-select-input"
    ) as NodeListOf<Element>;
    btn.forEach((element: Element) => {
      (element as HTMLElement).setAttribute("type", "button");
    });
  }, [distributionData, currencyData, subAccountList, paymentOptionValue]);
  const unitList =
    (caseData &&
      caseData?.caseUnits?.map((item: any) => ({
        name: item.name,
        id: item.id,
      }))) ||
    [];
  const selectedUnit = watch("selectedUnit");
  const accountUnit = watch("accountUnit");
  const subAccount = watch("subAccount");
  const enteredCreditorName = watch("creditorName");

  const handleViewIcon = (name: string) => {
    setShowField((prev: any) => ({ ...prev, [name]: !prev[name] }));
  };
  const handleSelect = (id: string) => {
    setCreditorContactInfo({});
    setValue("creditorId", "");
    const propData = {
      caseId: caseId || "",
      creditorId: id,
    };
    triggerCreditorSummary(propData)
      .unwrap()
      .then((data: any) => {
        setValue("creditorId", id);
        const primaryContact = data?.creditorContactList?.find(
          (contact: any) => contact.isPrimaryContact === true
        );
        setCreditorContactInfo(primaryContact || {});
      })
      .catch((error) => {
        setCreditorContactInfo({});
      });
  };
  const handleSearch = useCallback(
    (searchValue: string) => {
      searchValue = searchValue?.split("|")?.[0] || searchValue;
      if (searchValue.trim() !== "") {
        clearErrors("creditorName");
        setCreditorList([]);
        setCreditorContactInfo({});
        triggerCreditor({
          CaseId: caseId,
          SearchText: searchValue,
          PageNumber: 1,
          PageSize: 20,
        })
          .unwrap()
          .then((data: any) => {
            if (data?.results.length === 0) {
              setValue("creditorId", "");
              setError("creditorName", {
                type: "manual",
                message: t(
                  "pages.paymentStatus.paymentSetup.details.creditorNotExist"
                ),
              });
            }
            setCreditorList(
              data?.results?.map((item: any) => ({
                creditorName: `${item.creditorName} | ${item.creditorReference}`,
                creditorId: item.creditorId,
              })) || []
            );
            const matchedCreditor = data?.results?.find(
              (item: any) =>
                item.creditorId === getValues("creditorId") &&
                `${item.creditorName} | ${item.creditorReference}` ===
                  getValues("creditorName")
            );
            if (matchedCreditor) {
              setValue(
                "creditorName",
                `${matchedCreditor.creditorName} | ${matchedCreditor.creditorReference}`
              );
              handleSelect(matchedCreditor.creditorId);
            }
          })
          .catch((error) => {
            setCreditorList([]);
            setValue("creditorId", "");
          });
      } else {
        setValue("creditorId", "");
        setCreditorList([]);
        setCreditorContactInfo({});
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerCreditor, caseId, setValue]
  );
  useEffect(() => {
    const defaultValues = getValues();
    if (defaultValues.creditorName) {
      setCreditorValue(defaultValues.creditorName);
      handleSearch(defaultValues.creditorName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, handleSearch]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };
  const handleDetailsCancel = () => {
    setIsDiscardVisible(false);
    dispatch(setDiscardEditMode({ editMode: true }));
  };
  const handleDetailsReset = (event: any) => {
    event.preventDefault();
    reset();
    navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.PAYMENT}`);
    setIsDiscardVisible(false);
  };
  const onSubmit = async (data: any) => {
    if (
      !getValues("creditorId") ||
      Object.keys(creditorContactInfo).length === 0
    ) {
      setError("creditorName", {
        type: "required",
        message: t("pages.paymentStatus.paymentSetup.details.creditorRequired"),
      });
      return;
    }
    const isValid = await trigger(["creditorName"]);
    if (isValid) {
      goToNextTab();
    }
  };

  if (distributionDataLoading || isSubAccountListLoading)
    return <MotifProgressLoader show variant="default" />;
  if (distributionDataError || creditorError)
    return (
      <MotifErrorMessage>
        {t("global.globalNotifications.unExpectedError")}
      </MotifErrorMessage>
    );
  if (subAccountListError)
    return (
      <MotifErrorMessage>
        {t("pages.paymentStatus.paymentSetup.details.subAccountListError")}
      </MotifErrorMessage>
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div style={{ rowGap: "20px" }} className="motif-row pad-b-20">
        <div
          style={{ minWidth: "200px" }}
          className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
        >
          <MotifLabel>
            {t("pages.paymentStatus.paymentSetup.details.distribution")}
            <span className="required-input">*</span>
          </MotifLabel>
          <MotifFormField>
            <Controller
              name="distribution"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <MotifSelect
                  {...field}
                  //@ts-ignore
                  onChange={field.onChange}
                >
                  {distributionList.map((option: any) => (
                    <MotifOption key={option.id} value={option.id}>
                      {option.name}
                    </MotifOption>
                  ))}
                </MotifSelect>
              )}
            />
          </MotifFormField>

          {errors["distribution"] &&
            errors["distribution"].type === "required" && (
              <MotifErrorMessage>
                {t(
                  "pages.paymentStatus.paymentSetup.details.distributionRequired"
                )}
              </MotifErrorMessage>
            )}
        </div>
        <div
          style={{ minWidth: "200px" }}
          className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
        >
          <MotifLabel>
            {t("pages.paymentStatus.paymentSetup.details.subAccount")}
            <span className="required-input">*</span>
          </MotifLabel>
          <MotifFormField>
            <Controller
              name="subAccount"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <MotifSelect
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    const selectedOption = subAccountList.find(
                      (option: any) => option.subAccountId === value
                    );
                    setValue("accountUnit", selectedOption.unitName);
                    setValue("unitDecimalPlace", selectedOption.decimalPlace);
                  }}
                  filter={true}
                >
                  {subAccountList.map((option: any) => (
                    <MotifOption
                      key={option.subAccountId}
                      value={option.subAccountId}
                    >
                      {option.name}
                    </MotifOption>
                  ))}
                </MotifSelect>
              )}
            />
          </MotifFormField>
          {errors["subAccount"] && errors["subAccount"].type === "required" && (
            <MotifErrorMessage>
              {t("pages.paymentStatus.paymentSetup.details.subAccountRequired")}
            </MotifErrorMessage>
          )}
        </div>
        <div
          style={{ minWidth: "200px" }}
          className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
        >
          <MotifLabel>
            {t("pages.paymentStatus.paymentSetup.details.prefix")}
            <span className="required-input">*</span>
          </MotifLabel>
          <MotifFormField>
            <Controller
              name="prefix"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <MotifSelect
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                >
                  {prefixList.map((option: any) => (
                    <MotifOption key={option.value} value={option.value}>
                      {t(option.label)}
                    </MotifOption>
                  ))}
                </MotifSelect>
              )}
            />
          </MotifFormField>
          {errors["prefix"] && errors["prefix"].type === "required" && (
            <MotifErrorMessage>
              {t("pages.paymentStatus.paymentSetup.details.prefixReq")}
            </MotifErrorMessage>
          )}
        </div>
        <div
          style={{ minWidth: "200px" }}
          className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
        >
          <MotifLabel>
            {t("pages.paymentStatus.paymentSetup.details.generatePaymentBy")}
            <span className="required-input">*</span>
          </MotifLabel>
          <MotifFormField>
            <Controller
              name="paymentOption"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <MotifSelect
                  {...field}
                  //@ts-ignore
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("paymentAmount", "");
                    setValue("unit", "");
                    setValue("exchangeRate", 1);
                    setValue("claimItems", []);
                    setValue("generateClaimItemIds", []);
                    setValue("addedClaimItemIds", []);
                    setValue("generatePaymentItems", []);
                  }}
                >
                  <MotifOption value="0">
                    {t("pages.paymentStatus.paymentSetup.details.netPayment")}
                  </MotifOption>
                  <MotifOption value="1">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.upadateIndividualClaimItem"
                    )}
                  </MotifOption>
                </MotifSelect>
              )}
            />
          </MotifFormField>
          {errors["paymentOption"] &&
            errors["paymentOption"].type === "required" && (
              <MotifErrorMessage>
                {t(
                  "pages.paymentStatus.paymentSetup.details.generatePaymentRequired"
                )}
              </MotifErrorMessage>
            )}
        </div>
        {paymentOptionValue === "0" && (
          <>
            <div
              style={{ minWidth: "200px" }}
              className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
            >
              <MotifLabel>
                {t("pages.paymentStatus.paymentSetup.details.paymentAmount")}
                <span className="required-input">*</span>
              </MotifLabel>
              <MotifFormField>
                <Controller
                  name="paymentAmount"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^-?(?!0(\.0+)?$)\d*\.?\d/,
                      message: t(
                        "pages.paymentStatus.paymentSetup.details.invalidNumber"
                      ),
                    },
                  }}
                  render={({ field }) => (
                    <MotifInput
                      {...field}
                      //@ts-ignore
                      onChange={field.onChange}
                      value={field.value}
                      hideClearButton={true}
                    />
                  )}
                />
              </MotifFormField>
              {errors["paymentAmount"] &&
                errors["paymentAmount"].type === "required" && (
                  <MotifErrorMessage>
                    {t(
                      "pages.paymentStatus.paymentSetup.details.paymentAmountRequired"
                    )}
                  </MotifErrorMessage>
                )}
              {errors["paymentAmount"] &&
                errors["paymentAmount"].type === "pattern" && (
                  <MotifErrorMessage>
                    {t(
                      "pages.paymentStatus.paymentSetup.details.invalidNonZeroNumber"
                    )}
                  </MotifErrorMessage>
                )}
            </div>
            <div
              style={{ minWidth: "200px" }}
              className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
            >
              <MotifLabel>
                {t("pages.paymentStatus.paymentSetup.details.unit")}
                <span className="required-input">*</span>
              </MotifLabel>
              <MotifFormField>
                <Controller
                  name="unit"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MotifSelect
                      {...field}
                      onChange={(value) => {
                        field.onChange(value);
                        const selectedOption = unitList.find(
                          (option: any) => option.id === value
                        );
                        setValue("selectedUnit", selectedOption.name);
                      }}
                    >
                      {unitList.map((option: any) => (
                        <MotifOption key={option.id} value={option.id}>
                          {option.name}
                        </MotifOption>
                      ))}
                    </MotifSelect>
                  )}
                />
              </MotifFormField>
              {errors["unit"] && errors["unit"].type === "required" && (
                <MotifErrorMessage>
                  {t("pages.paymentStatus.paymentSetup.details.unitRequired")}
                </MotifErrorMessage>
              )}
            </div>
            <div
              style={{ minWidth: "200px" }}
              className="motif-col-xs-1  motif-col-md-2 motif-col-lg-4"
            >
              <MotifLabel>
                {t("pages.paymentStatus.paymentSetup.details.exchangeRate", {
                  selectedUnit: selectedUnit,
                  accountUnit: accountUnit
                    ? accountUnit
                    : subAccount
                    ? "Account Unit Undefined"
                    : accountUnit,
                })}
                <span className="required-input">*</span>
              </MotifLabel>
              <MotifFormField>
                <Controller
                  name="exchangeRate"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^(?!0(\.0+)?$)\d+(\.\d+)?$/,
                      message: t(
                        "pages.paymentStatus.paymentSetup.details.invalidNumber"
                      ),
                    },
                  }}
                  render={({ field }) => (
                    <MotifInput
                      {...field}
                      //@ts-ignore
                      onChange={field.onChange}
                      value={field.value}
                      hideClearButton={true}
                    />
                  )}
                />
              </MotifFormField>
              {errors["exchangeRate"] &&
                errors["exchangeRate"].type === "required" && (
                  <MotifErrorMessage>
                    {t(
                      "pages.paymentStatus.paymentSetup.details.exchangRateRequired"
                    )}
                  </MotifErrorMessage>
                )}
              {errors["exchangeRate"] &&
                errors["exchangeRate"].type === "pattern" && (
                  <MotifErrorMessage>
                    {t(
                      "pages.paymentStatus.paymentSetup.details.invalidNumber"
                    )}
                  </MotifErrorMessage>
                )}
            </div>
          </>
        )}
      </div>

      <div className="motif-row pad-b-20">
        <div className="motif-col-lg-7">
          <MotifLabel>
            {t("pages.paymentStatus.paymentSetup.details.selectCreditor")}
            <span className="required-input">*</span>
          </MotifLabel>
          <MotifFormField>
            <Controller
              name="creditorName"
              control={control}
              rules={{
                required: t(
                  "pages.paymentStatus.paymentSetup.details.creditorRequired"
                ),
              }}
              render={({ field: { onChange, value } }) => {
                return (
                  <div onKeyDown={handleKeyDown}>
                    <SearchInput
                      id="creditorName"
                      items={creditorList}
                      value={value}
                      open={!creditorLoading}
                      hideClearButton={true}
                      onChange={(e: any) => {
                        let value =
                          e.target.value?.split("|")?.[0] || e.target.value;
                        setCreditorValue(value);
                        setCreditorContactInfo({});

                        onChange(value);
                      }}
                      filterPropFunction={(e: any) => {
                        return e?.creditorName;
                      }}
                      onEnter={() => {
                        if (creditorValue?.length < 3) {
                          setCreditorList([]);
                          return
                        };
                        handleSearch(creditorValue);
                      }}
                      withOutDebounce={true}
                      renderValue={true}
                      onSelect={(value: any) => {
                        setValue("claimItems", []);
                        setValue("generateClaimItemIds", []);
                        setValue("addedClaimItemIds", []);
                        setValue("generatePaymentItems", []);
                        setValue("creditorId", value.creditorId);
                        setCreditorValue(value.creditorName);
                        handleSelect(value.creditorId);
                        return onChange(value.creditorName);
                      }}
                    />
                  </div>
                );
              }}
            />
          </MotifFormField>
          {enteredCreditorName?.length < 3 && (
            <MotifLabel>
              {t("global.globalNotifications.searchActivateCharacterLimit", {
                labelName: "Creditor",
              })}
            </MotifLabel>
          )}
          {creditorLoading && (
            <MotifLabel>
              {t("global.globalNotifications.loadingMessage")}
            </MotifLabel>
          )}
          {errors["creditorName"] &&
            errors["creditorName"].type === "required" && (
              <MotifErrorMessage>
                {t("pages.paymentStatus.paymentSetup.details.creditorRequired")}
              </MotifErrorMessage>
            )}
          {errors["creditorName"] &&
            errors["creditorName"].type === "manual" && (
              <MotifErrorMessage>
                {t("pages.paymentStatus.paymentSetup.details.creditorNotExist")}
              </MotifErrorMessage>
            )}
        </div>
      </div>
      {isCreditorSummaryFetching ? (
        <MotifProgressLoader show variant="default" />
      ) : creditorSummaryError ? (
        <MotifErrorMessage>
          {t("global.globalNotifications.wentWrong")}
        </MotifErrorMessage>
      ) : (
        Object.keys(creditorContactInfo).length !== 0 && (
          <>
            <div className="crediitor-info-section">
              <div className="motif-container section-container-border contact-details pad-t-20 pad-b-30 pad-l-40">
                <div
                  className="motif-row pad-b-10 contact-selection"
                  style={{ alignItems: "center" }}
                >
                  <div className="motif-col-lg-3">
                    <div className="motif-body2-default-bold">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.payeeName",
                        {
                          payeeName:
                            creditorContactInfo?.firstName +
                            " " +
                            creditorContactInfo?.lastName,
                        }
                      )}
                    </div>
                  </div>
                  <div className="motif-col-lg-4">
                    <MotifChip
                      dotSide="left"
                      type="chip"
                      variant="monochrome-dark"
                    >
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.primary"
                      )}
                    </MotifChip>
                  </div>
                </div>
                <div className="motif-row pad-t-30">
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.additionalPayeeType"
                      )}
                    </div>
                    <div className="motif-body2-default-bold line-break">-</div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.firstName"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.firstName}
                    </div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.lastName"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.lastName}
                    </div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.prefix"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.prefix || "-"}
                    </div>
                  </div>
                  <div className="motif-col-lg-3"></div>
                </div>
                <div className="motif-row pad-t-30">
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.email"
                      )}
                    </div>
                    <div className="motif-body2-default-bold line-break">
                      {creditorContactInfo.email}
                    </div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.phoneNumber"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.phone}
                    </div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.fax"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.fax}
                    </div>
                  </div>
                  <div className="motif-col-lg-3">
                    <div className="title">
                      {t(
                        "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.relationshipToCreditor"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {creditorContactInfo.relationshipToCreditor}
                    </div>
                  </div>
                  <div className="motif-col-lg-3"></div>
                </div>
              </div>
            </div>
            <div className="motif-container section-container-border address-details mar-t-20 pad-t-20 pad-b-30 pad-l-40">
              <div
                className="motif-row pad-b-10 address-selection"
                style={{ alignItems: "center" }}
              >
                <div className="motif-col-lg-3">
                  <div className="motif-body2-default-bold">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.address"
                    )}
                  </div>
                </div>
              </div>
              <div className="motif-row pad-t-30">
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.address1"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.address1}
                  </div>
                </div>
                <div className="motif-col-lg-3"></div>
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.address2"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.address2}
                  </div>
                </div>
                <div className="motif-col-lg-3"></div>
              </div>
              <div className="motif-row pad-t-30">
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.city"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.city}
                  </div>
                </div>
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.provinceState"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.provinceStateCode ||
                      creditorContactInfo.provinceState}
                  </div>
                </div>
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.postalCode"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.postalCode}
                  </div>
                </div>
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.country"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {creditorContactInfo.country}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-container section-container-border address-details mar-t-20 pad-t-20 pad-b-30 pad-l-40">
              <div
                className="motif-row pad-b-10 address-selection"
                style={{ alignItems: "center" }}
              >
                <div className="motif-col-lg-3">
                  <div className="motif-body2-default-bold">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.paymentDetails"
                    )}
                  </div>
                </div>
              </div>
              <div className="motif-row pad-t-30 payment-fields">
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.institutionNumber"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.institutionNumber}
                    name={"institutionNumber"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
                <div className="motif-col-lg-2">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.transitNumber"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.transitNumber}
                    name={"transitNumber"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
                <div className="motif-col-lg-2">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.accountNumber"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.accountNumber}
                    name={"accountNumber"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
                <div className="motif-col-lg-2">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.IBANNumber"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.ibanNumber}
                    name={"ibanNumber"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
                <div className="motif-col-lg-2">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.routingCode"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.routingCode}
                    name={"routingCode"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
              </div>
              <div className="motif-row pad-t-30 payment-fields">
                <div className="motif-col-lg-3">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.SWIFTCode"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.swiftCode}
                    name={"swiftCode"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
                <div className="motif-col-lg-2">
                  <div className="title">
                    {t(
                      "pages.paymentStatus.paymentSetup.details.creditorSummaryInfo.ACHCode"
                    )}
                  </div>
                  <MaskedInput
                    value={creditorContactInfo.achCode}
                    name={"achCode"}
                    index={0}
                    handleViewIcon={handleViewIcon}
                    showField={showField}
                  />
                </div>
              </div>
            </div>
          </>
        )
      )}
      <div className="display-flex justify-content-end mar-t-20 button-container sticky-footer">
        <div className="pad-r-10">
          <MotifButton
            type="button"
            variant={"secondary"}
            onClick={() => {
              if (isDirty) {
                dispatch(setDiscardEditMode({ editMode: false }));
                setIsDiscardVisible(true);
              } else {
                navigate(
                  `/case/${caseId}?activeTab=${ActiveTabsOrder.PAYMENT}`
                );
              }
            }}
          >
            {t("global.globalActions.cancel")}
          </MotifButton>
        </div>

        <div>
          <MotifButton variant="primary" disabled={isCreditorSummaryFetching}>
            {t("global.globalActions.next")}
          </MotifButton>
        </div>
      </div>
      <ConfirmationModal
        isVisible={isDiscardVisible}
        setIsVisible={setIsDiscardVisible}
        cancelBtnApproveHandler={handleDetailsReset}
        cancelBtnRejectHandler={handleDetailsCancel}
      />
    </form>
  );
};
export default Details;
